import * as React from 'react';
import { styled } from '@mui/material/styles';
import Editor from '../editor/Editor';
import { observer } from 'mobx-react';
import NodeLayer, {
  questionTypes,
  narrativeSplitPoint,
} from '../editor/NodeLayer';
import { observable } from 'mobx';
import { RadioGroup, Radio } from 'react-radio-group';
import CheckboxGroup from 'react-checkbox-group';
import ConnectionLayer from '../editor/ConnectionLayer';
import CustomLinkify from '../pages/questionnaire/components/CustomLinkify';

const StyledRadioGroup = styled(RadioGroup)({
  margin: '12px',
  fontSize: '14',
  color: 'rgb(120, 120, 120)',
});

const CheckboxContainer = styled('div')({
  margin: '12px',
  fontSize: '14',
  color: 'rgb(120, 120, 120)',
});

const QuestionText = styled('div')({
  margin: '12px',
  fontSize: '32px',
  minHeight: '32px',
  color: 'rgb(80, 80, 80)',
});

const TextInput = styled('input')({
  outline: 'none',
  border: '1px solid rgb(220, 220, 220)',
  fontSize: '12px',
  padding: '8px',
  color: 'rgb(80, 80, 80)',
  resize: 'none',
  margin: '12px',
  width: '320px',

  '&:focus': {
    border: '1px solid rgb(180, 180, 180)',
  },
});

export class QuestionnaireState {
  @observable
  currentQuestionNode: NodeLayer;

  @observable
  questionsArray: NodeLayer[] = [];

  @observable
  currentQuestionPointer: number = 0;

  get isComplete(): boolean {
    return this.currentQuestionNode == null;
  }

  constructor(startLayer: NodeLayer) {
    this.currentQuestionNode = startLayer;
  }

  isConditionValid(conn: ConnectionLayer): boolean {
    if (conn.condition == '') {
      return true;
    }
    var conditionList = conn.condition.split(narrativeSplitPoint);

    var intersect = this.currentQuestionNode.answers.filter((it) =>
      conditionList.includes(it)
    );

    return intersect.length != 0;
  }

  handleNext() {
    if (this.currentQuestionNode == null) {
      return;
    }

    for (var conn of this.currentQuestionNode.outgoingConnections) {
      if (this.isConditionValid(conn)) {
        this.questionsArray[this.currentQuestionPointer] =
          this.currentQuestionNode;
        this.currentQuestionNode = conn.endNode;
        this.currentQuestionPointer++;
        return;
      }
    }

    this.currentQuestionNode = null;
  }

  handlePrevious() {
    if (this.currentQuestionNode == null) {
      return;
    }
    if (this.currentQuestionPointer > 0 && this.questionsArray.length >= 1) {
      this.currentQuestionNode =
        this.questionsArray[--this.currentQuestionPointer];
    }
  }
}

export type QuestionnaireRendererProps = {
  editor: Editor;
  className?: string;
  style?: any;
  state: QuestionnaireState;
  center?: boolean;
};

@observer
export default class QuestionnaireRenderer extends React.Component<QuestionnaireRendererProps> {
  getCurrentQuestionText(): string {
    var { state } = this.props;

    if (state.currentQuestionNode == null) {
      return '';
    }

    return state.currentQuestionNode.questionText;
  }

  renderAnswerSelector() {
    var { state } = this.props;

    if (state.currentQuestionNode == null) {
      return null;
    }

    if (state.currentQuestionNode.questionType == questionTypes.text) {
      return (
        <TextInput
          key="answer-text"
          type="text"
          value={state.currentQuestionNode.answer}
          onChange={(ev) =>
            state.currentQuestionNode.setAnswer(ev.target.value)
          }
        />
      );
    }

    if (state.currentQuestionNode.questionType == questionTypes.yesNo) {
      return (
        <StyledRadioGroup
          key="answer-yesno"
          name="answer-yesno"
          selectedValue={state.currentQuestionNode.answer}
          onChange={(ev) => state.currentQuestionNode.setAnswer(ev)}
        >
          <div style={{ marginBottom: '12px' }}>
            <Radio value="Yes" />
            <span>Yes </span>
          </div>
          <div>
            <Radio value="No" />
            <span>No</span>
          </div>
        </StyledRadioGroup>
      );
    }

    if (
      state.currentQuestionNode.questionType == questionTypes.singleChoice ||
      state.currentQuestionNode.questionType == 'Single Choice'
    ) {
      return (
        <StyledRadioGroup
          key="answer-singlechoice"
          name="answer-singlechoice"
          selectedValue={state.currentQuestionNode.answer}
          onChange={(ev) => state.currentQuestionNode.setAnswer(ev)}
        >
          {state.currentQuestionNode.choices.map((choice, index) => (
            <div style={{ marginBottom: '12px' }} key={`radio-${index}`}>
              <Radio value={choice} />
              <span> {choice} </span>
            </div>
          ))}
        </StyledRadioGroup>
      );
    }

    if (
      state.currentQuestionNode.questionType == questionTypes.multiChoice ||
      state.currentQuestionNode.questionType == 'Multiple Choice'
    ) {
      return (
        <CheckboxGroup
          name="fruits"
          value={state.currentQuestionNode.answers}
          onChange={(ev) => state.currentQuestionNode.setAnswers(ev)}
        >
          {(Checkbox) => (
            <CheckboxContainer>
              {state.currentQuestionNode.choices.map((choice, index) => (
                <div style={{ marginBottom: '12px' }} key={`check-${index}`}>
                  <Checkbox value={choice} />
                  <span> {choice} </span>
                </div>
              ))}
            </CheckboxContainer>
          )}
        </CheckboxGroup>
      );
    }
  }

  render() {
    var styles: any = {};

    if (this.props.center) {
      styles.display = 'flex';
      styles.flexDirection = 'column';
      styles.alignItems = 'center';
    }

    return (
      <div
        className={this.props.className}
        style={{
          position: 'relative',
          ...styles,
          ...this.props.style,
        }}
      >
        <QuestionText>
          <CustomLinkify str={this.getCurrentQuestionText()} />
        </QuestionText>
        {this.renderAnswerSelector()}
      </div>
    );
  }
}
